import React from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import RoutesPath from "../../../utils/Paths";

/**
 * HowItWorksSection
 * Rendered right after the map / HomeStyled block, before <Footer />.
 * Mirrors the "From search to hire in three steps" screenshot.
 */
const STEPS = [
  {
    number: "1",
    title: "Search the map",
    description:
      "Open the map and explore freelancers, local service pros, and job posts near you — or anywhere in the world.",
  },
  {
    number: "2",
    title: "Compare & connect",
    description:
      "Filter by service, skill level, and budget, then message the people who fit your project or role directly.",
  },
  {
    number: "3",
    title: "Hire or get hired",
    description:
      "Agree on scope, get the work done, and build your reputation. Customers, sellers, and employers all in one place.",
  },
];

const HowItWorksSection = () => {
  const navigate = useNavigate();

  return (
    <SectionStyled>
      <div className="container py-5 mt-0">
        {/* Header */}
        <div className="text-center mb-5">
          <p className="kicker mb-2">HOW IT WORKS</p>
          <h2 className="section-title mb-3">From search to hire in three steps</h2>
          <p className="section-subtitle mx-auto">
            Whether you're hiring help, offering a service, or looking for work,
            GigFalcon works the same simple way.
          </p>
        </div>

        {/* Step cards */}
        <div className="row g-4 justify-content-center">
          {STEPS.map((step) => (
            <div key={step.number} className="col-12 col-md-4">
              <div className="step-card h-100">
                <div className="step-badge mb-3">{step.number}</div>
                <h5 className="step-title mb-2">{step.title}</h5>
                <p className="step-desc mb-0">{step.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </SectionStyled>
  );
};

export default HowItWorksSection;

/* ── Styles ─────────────────────────────────────────────────────────────── */
const TEAL = "#0fd3bb";
const DARK = "#222222";
const GREY = "#666666";

const SectionStyled = styled.section`
  background-color: #f7f2ec;

  .kicker {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 2.5px;
    color: ${TEAL};
    text-transform: uppercase;
  }

  .section-title {
    font-size: clamp(1.6rem, 3.5vw, 2.4rem);
    font-weight: 800;
    color: ${DARK};
    font-family: "MS_Bold", "Arial Black", sans-serif;
    line-height: 1.2;
  }

  .section-subtitle {
    font-size: 1rem;
    color: ${GREY};
    max-width: 480px;
    line-height: 1.6;
  }

  /* ── Step cards ── */
  .step-card {
    background: #ffffff;
    border-radius: 16px;
    padding: 28px 24px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
    transition: transform 0.2s ease, box-shadow 0.2s ease;

    &:hover {
      transform: translateY(-4px);
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
    }
  }

  .step-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background-color: ${TEAL};
    color: #ffffff;
    font-size: 16px;
    font-weight: 800;
  }

  .step-title {
    font-size: 17px;
    font-weight: 700;
    color: ${DARK};
    font-family: "MS_Bold", "Arial Black", sans-serif;
  }

  .step-desc {
    font-size: 14px;
    color: ${GREY};
    line-height: 1.6;
  }

  /* ── CTA buttons ── */
  .cta-btn {
    background-color: ${TEAL};
    color: #ffffff;
    border: none;
    padding: 12px 28px;
    border-radius: 8px;
    font-size: 15px;
    font-weight: 700;
    cursor: pointer;
    transition: background-color 0.2s ease;

    &:hover {
      background-color: #0bbea9;
    }
  }

  .cta-btn-outline {
    background-color: transparent;
    color: ${DARK};
    border: 1.5px solid #cccccc;
    padding: 12px 28px;
    border-radius: 8px;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
    transition: border-color 0.2s ease, color 0.2s ease;

    &:hover {
      border-color: ${TEAL};
      color: ${TEAL};
    }
  }

  @media (max-width: 767px) {
    .cta-btn,
    .cta-btn-outline {
      display: block;
      width: 100%;
      max-width: 280px;
      margin: 0 auto 12px auto;
    }
    .cta-btn {
      margin-bottom: 12px !important;
    }
  }
`;
